<template>
  <div class="event-card-container" :style="cssVars">
    <v-card :class="cardClass" :to="to" flat>
      <picture>
        <img
          @error="onError"
          loading="lazy"
          :src="parsedImg"
          :alt="title"
          width="100%"
        />
      </picture>

      <div class="event-card_title">
        {{ parsedTitle }}
        <br />
        <div
          v-if="!hideDate && parsedDate"
          class="d-inline-flex align-center font-weight-regular"
        >
          <v-icon size="12" color="inherit" left>fas fa-calendar-alt</v-icon>
          {{ parsedDate }}
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { formatToURL } from "../../utils/formatToURL";
import DefaultImg from "../../assets/img/default-img.png";

export default {
  props: {
    id: {
      type: Number,
      required: true,
    },
    img: {
      type: String,
      default: DefaultImg,
    },
    title: {
      type: String,
      required: true,
    },
    date: {
      type: String,
    },
    type: {
      type: String,
      validator: (val) => ["vertical", "horizontal"].includes(val),
      default: "vertical",
    },
    dark: {
      type: Boolean,
      default: false,
    },
    aspectRatio: {
      type: Number,
    },
    basePath: {
      type: String,
      default: "/coberturas",
    },
    hideDate: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    width: null,
  }),
  computed: {
    cardClass() {
      return ["event-card transparent", "event-card--" + this.type];
    },
    cssVars() {
      const cssVars = {
        "--title-color": !this.dark ? "#000000" : "#fff",
        "--hover-title-color": !this.dark ? "var(--v-primary-darken1)" : "#ccc",
        "--background-color": !this.dark ? "var(--v-primary-base)" : "#fff",
        "--dot-color": !this.dark
          ? "var(--v-primary-base)"
          : "var(--v-primary-base)",
      };

      if (this.aspectRatio) {
        cssVars["--aspect-ratio"] = this.aspectRatio;
      } else {
        cssVars["--aspect-ratio"] = this.type === "horizontal" ? 4 / 3 : 16 / 9;
      }

      return cssVars;
    },
    parsedTitle() {
      return this.title?.replace(/\\"/g, '"');
    },
    parsedImg() {
      if (!this.width) {
        return null;
      }

      let img = "";

      if (!/^https?:\/\//.test(this.img)) {
        img = this.files;
      }

      img += this.img;

      return `${img}?resize=1&w=${this.width}`;
    },
    parsedDate() {
      return this.date ? new Date(this.date).toLocaleDateString() : null;
    },
    to() {
      const slug = formatToURL(this.title);

      return this.basePath + `/${slug}-${this.id}`;
    },
  },
  methods: {
    onError(e) {
      e.target.src = DefaultImg;
    },
    setWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          this.width = 1300 / 2;
          break;
        case "sm":
          this.width = 960 / 2;
          break;
        default:
          this.width = 600;
      }
    },
  },
  mounted() {
    this.setWidth();
  },
};
</script>

<style lang="scss">
@property --overlay-color {
  syntax: "<color>";
  initial-value: rgba(0, 0, 0, 0);
  inherits: false;
}

.event-card-container {
  container-name: event-card;
  container-type: inline-size;
}

.event-card {
  display: grid !important;
  overflow: hidden;
  border-radius: 0 !important;

  img {
    object-fit: cover;
    object-position: center;
    transition: 0.2s;
    height: 100%;
    width: 100%;
  }

  picture {
    display: flex;
    position: relative;
    overflow: hidden;
    aspect-ratio: var(--aspect-ratio);
    max-width: 100%;
    border-radius: 0 !important;
  }

  picture::after {
    content: "";
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(transparent 50%, var(--overlay-color) 100%);
    transition: --overlay-color 0.3s;
    border-radius: 0 !important;
    z-index: 9;
  }

  &_title {
    font-size: 16px !important;
    font-weight: 520;
    line-height: "normal";
    letter-spacing: 0.15px !important;
    font-family: "Raleway", sans-serif !important;
    transition: 0.3s;
    color: var(--title-color);
    padding: 8px;

    i {
      color: inherit !important;
    }
  }

  &:hover {
    picture::after {
      --overlay-color: rgba(0, 0, 0, 0.4);
    }

    img {
      transform: scale(1.1);
    }

    .event-card_title {
      color: var(--hover-title-color);
    }
  }

  @container event-card (max-width: 300px) {
    &_title {
      font-size: 14px !important;
    }
  }
}

.event-card--vertical {
  grid-template-columns: auto;

  .event-card_title {
    @container event-card (min-width: 300px) {
      font-size: 16px !important;
    }
    @container event-card (min-width: 400px) {
      font-size: 20px !important;
    }
  }
}

.event-card--horizontal {
  grid-template-columns: max-content auto;
  gap: 8px;

  picture {
    max-height: 100px;
    width: 100%;
  }

  .event-card_title {
    padding-top: 0px;
    padding-bottom: 0px;
  }

  @container event-card (max-width: 300px) {
    img {
      display: none !important;
    }

    .event-card_title::before {
      display: inline-block;
      content: "";
      background-color: var(--dot-color);
      margin-right: 4px;
      margin-bottom: 3px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }
  }
}
</style>
